/* eslint-disable no-extra-parens */
/* global React */
import { darken, Fab, useTheme } from '@mui/material'
import { getThemeColor, isObj } from 'lib/util'
import { useMemo } from 'react'
import { useMemoRef, useStyles } from '@platform/react/hook'
import Icon from 'ui/Element/Icon'
import Text from 'ui/Element/Text'

const fontSizeMapping = {
  tiny: '12/medium',
  small: '12/medium',
  medium: '14/bold',
  large: '16/medium',
  huge: '16/medium',
}

const iconSizeMapping = {
  tiny: '1.25rem',
  small: '1.25rem',
  medium: '1.5rem',
  large: '1.5rem',
  huge: '1.5rem',
}

const sizeMapping = {
  tiny: '1.5rem',
  small: '2rem',
  medium: '2.5rem',
  large: '3rem',
  huge: '3.5rem',
}

const styles = (theme, {
  disabled,
  outline,
  fullWidth,
  contentStart,
  color,
  background,
  size,
}) => ({
  root: {
    cursor: 'pointer',
    boxShadow: 'none',
    backgroundColor: background || 'transparent',
    color: `${color}!important`,
    transition: 'background-color 250ms ease',

    ...outline && {
      border: `${outline}px solid ${theme.palette.divider}`,
      boxShadow: '0 0.25rem 0.375rem 0 rgb(0 0 0 / 5%)',
    },

    width: sizeMapping[size || 'medium'],
    height: sizeMapping[size || 'medium'],

    minWidth: sizeMapping.tiny,
    minHeight: sizeMapping.tiny,

    ...fullWidth && { width: '100%' },
    ...contentStart && { justifyContent: 'flex-start' },
    ...disabled && {
      backgroundColor: theme.palette.gray[950],
    },

    '&:active': {
      boxShadow: 'none',
    },
    '&:hover': {
      boxShadow: 'none',
      backgroundColor: background
        ? darken(getThemeColor(theme, background), 0.1)
        : theme.palette.border.main,

      // Reset on touch devices, it doesn't add specificity
      '@media (hover: none)': {
        backgroundColor: 'transparent',
      },
      textDecoration: 'none',
    },
  },
  text: {
    marginLeft: '0.5rem',
  },
  ...theme.custom.fabButton,
})

const _getColor = (theme, [icon, background]) => [
  icon.includes('.') ? getThemeColor(theme, icon) : theme.palette[icon]?.contrastText,
  background.includes('.') ? getThemeColor(theme, background) : theme.palette[background]?.main,
]

/**
 * Floating Action Button
 *
 * Will display a floating action button, using {@link Icon} under the hood.
 *
 * @param {Object} forwardedRef       a reference to the inner root element
 * @param {Object|string} icon        the button's icon props or name
 * @param {string} value              the button's text
 * @param {Object} events             an object containing the element's event handlers
 * @param {function} events.onClick   the event handler to be called whenever the button is clicked
 * @param {boolean} disabled          whether the button should be grayed out and not clickable
 * @param {string} variant            the variant to use
 * @param {string} [color]            the color of the button's icon
 * @param {string} [background]       background color to use for the button
 * @param {string} [size='medium']    named size mapped to a rem value for the icon
 * @param {Object} [iconProps]        props for the icon
 * @param {Object} props              additional element's properties
 * @param {number} props.outline      the number of pixels of width of the element's outline
 * @param {boolean} props.fullWidth   whether the element should have the same width as its parent
 * @returns {JSX.Element}             a new FabButton element
 */
const FabButton = ({
  forwardedRef,
  icon,
  value,
  events,
  disabled,
  variant,
  color = 'secondary',
  background = color,
  size = 'medium',
  iconProps = {},
  ...props
}) => {
  const theme = useTheme()
  const { outline, fullWidth, contentStart } = props

  const [iconColor = 'black.main', backgroundColor = 'gray.950'] = useMemo(
    () => _getColor(theme, [color, background]),
    [color, background],
  )

  const classes = { ...useStyles(styles, {
    disabled,
    outline,
    fullWidth,
    contentStart,
    color: iconColor,
    background: backgroundColor,
    size
    ,
  })(),
  ...props.classes }

  const handleClick = event => events.onClick(event)

  return (
    <Fab
      variant={variant}
      disabled={disabled}
      onClick={handleClick}
      className={`${classes.root} ${classes?.button || ''}`}
    >
      <Icon
        source={{
          ...iconProps,
          ...isObj(icon) && icon,
          name: icon?.name || icon,
          color: disabled ? theme.palette.gray[600] : iconColor,
          size: iconSizeMapping[size] || iconSizeMapping.medium,
        }}
      />
      {!value ? null : (
        <Text
          value={value}
          className={classes.text}
          variant={fontSizeMapping[size] || '12/medium'}
        />
      )}
    </Fab>
  )
}

export default useMemoRef(FabButton, props => [
  props.value,
  props.disabled,
  props.color,
  props.background,
  props.icon,
  props.size,
  props.iconProps,
])
